import { useCallback, useEffect, useRef, useState } from "react";

type UseServerSearchOptions = {
  /** Called when search should trigger (after debounce or on Enter). Caller should reload page 1. */
  onSearch: () => void;
  /** Debounce delay in ms (default: 1500) */
  debounceMs?: number;
};

/**
 * Manages server-side search state with debounced input and immediate submit.
 *
 * - `searchQuery` — current input value (for controlled input)
 * - `searchQueryRef` — always-current value (safe to read inside async load functions)
 * - `handleSearchChange` — call on every keystroke; debounces the API trigger
 * - `handleSearchSubmit` — call on Enter; fires immediately, cancels pending debounce
 */
function getInitialSearch(): string {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get("search") ?? "";
}

function syncSearchToURL(value: string) {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  if (value) {
    params.set("search", value);
  } else {
    params.delete("search");
  }
  let newUrl = window.location.pathname;
  if (params.size) {
    newUrl += '?' + params.toString();
  }
  newUrl += window.location.hash;
  window.history.replaceState(null, "", newUrl);
}

export function useServerSearch({ onSearch, debounceMs = 1500 }: UseServerSearchOptions) {
  const [searchQuery, setSearchQuery] = useState(getInitialSearch);
  const searchQueryRef = useRef(getInitialSearch());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSearchRef = useRef(onSearch);

  // Keep refs in sync
  useEffect(() => { onSearchRef.current = onSearch; }, [onSearch]);
  useEffect(() => { searchQueryRef.current = searchQuery; }, [searchQuery]);

  // Cleanup debounce on unmount
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    searchQueryRef.current = value;
    syncSearchToURL(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      onSearchRef.current();
    }, debounceMs);
  }, [debounceMs]);

  const handleSearchSubmit = useCallback((value: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    searchQueryRef.current = value;
    setSearchQuery(value);
    syncSearchToURL(value);
    onSearchRef.current();
  }, []);

  return { searchQuery, searchQueryRef, handleSearchChange, handleSearchSubmit } as const;
}
